import { useEffect, useMemo, useState } from "react";
import { Autocomplete, Button, Chip, TextField, Typography } from "@mui/material";
import { useImmer } from "use-immer";
import { APP_BASE_URL, ASSET_CODAL_EXPIRE, extractFilters } from "../../Common/EndPoints";
import axios from "axios";
import toast from "react-hot-toast";
import { GridColDef } from "@mui/x-data-grid";
import DataTable from "../DataGrid/DataGrid";
import { expiryAssetsFixedColumns } from "./MidlifeRehabilitation";
import { getLocalStorage, getParsedLocalStorage } from "../../Utils/storage-utils";
import { extractUserDataFromPermission } from "../../Utils/Utils";
import dayjs from "dayjs";
import { DatePicker } from "antd";
import "./mis.scss";
import { useSearchParams } from "react-router-dom";
import { AutocompleteStationListPop } from "../AutocompleteStationListPop/AutocompleteStationListPop";
import { usePermissions } from "../../hooks/usePermissions";

const { RangePicker } = DatePicker;

const initialState = {
  data: [],
  totalElements: 0,
};

const initialFilters = {
  page: 0,
  pageSize: 10,
};

function CodalLifeExpiredAsset() {
  const [searchParams] = useSearchParams();
  const stationId = searchParams.get("stn_codes");
  const [state, setState] = useImmer(initialState);
  const [filter, setFilter] = useState<any>({
    ...initialFilters,
    customer: stationId?.split?.(",") || []
  });
  const [codalLifeExpiredAssetTableLoader, setCodalLifeExpiredAssetTableLoader] = useState(false);

  useEffect(() => {
    getCodalLifeExpiredAsset();
  }, [filter]);

  const codalLifeExpiredAssetColumns: GridColDef[] = useMemo(() => {
    return [
      ...expiryAssetsFixedColumns,
      {
        field: "doe",
        headerName: "Date of Expiry",
        sortable: true,
        minWidth: 150,
        hideable: true,
        flex: 1,
      },
    ];
  }, []);

  const getCodalLifeExpiredAsset = async () => {
    try {
      setCodalLifeExpiredAssetTableLoader(true);
      const _filter = {
        ...filter,
        expireDateFrom: filter?.expireDateFrom ? dayjs(filter?.expireDateFrom).format("YYYY-MM-DD") : "",
        expireDateTo: filter?.expireDateTo ? dayjs(filter?.expireDateTo).format("YYYY-MM-DD") : "",
        softwareIds: filter?.systems?.map((system: any) => system.systemId) || [],
      };
      delete _filter["systems"];
      const url = `${APP_BASE_URL}${ASSET_CODAL_EXPIRE}?${extractFilters(_filter)}`;
      const res = await axios.get(url);
      if (res?.data?.data?.content) {
        setState((state) => {
          state.data = res.data.data.content || [];
          state.totalElements = res.data.data?.totalElements || 0;
        });
      }
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        toast.error(error?.response?.data?.error?.message || "Something went wrong");
        return;
      }
      toast.error("Something went wrong");
    } finally {
      setCodalLifeExpiredAssetTableLoader(false);
    }
  };

  const filterTableData = (filter: any) => {
    setFilter((oldFilter: any) => ({
      ...oldFilter,
      ...filter,
      page: 0,
    }));
  };

  return (
    <div className="page-container--card">
      <div className="flex justify-between">
        <Typography variant="h6" sx={{ fontWeight: "bold" }} textAlign={"start"}>
          Codal Life Outlived
        </Typography>
      </div>

      <CodalLifeExpiredFilter filter={filter} onFilter={filterTableData} initialFilters={initialFilters} />

      <DataTable
        tableInfo={{
          id: `codal-life-expired-asset-table`,
          label: `Codal Life Expired Asset`,
          requireAuthSign: false,
          timestamp: { startTs: null, endTs: null },
        }}
        loading={codalLifeExpiredAssetTableLoader}
        rows={state.data}
        columns={codalLifeExpiredAssetColumns}
        rowCount={state.totalElements}
        pageSize={filter.pageSize}
        pageSizeChange={(pageSize: number) =>
          setFilter(() => ({
            ...filter,
            pageSize,
          }))
        }
        pageIndex={filter.page}
        pageChange={(page: number) =>
          setFilter(() => ({
            ...filter,
            page,
          }))
        }
        hideExportPDF={true}
        paginationMode="server"
      />
    </div>
  );
}

export default CodalLifeExpiredAsset;

const CodalLifeExpiredFilter = ({
  filter,
  onFilter,
  initialFilters,
}: {
  filter: any;
  onFilter: any;
  initialFilters: any;
}) => {
  const [privateFilter, setPrivateFilter] = useImmer(filter);
  const system_options = useMemo(() => {
    const systems = getParsedLocalStorage("systems_list", []);
    return systems.map((system: any) => ({
      systemId: system.id,
      systemName: system.code,
    }));
  }, []);
  const { outerToInnerKeys: divisionLists, innerToCustomerNames: station, outermostKeys: zone } = usePermissions();
  // const { divisionLists, station, zone } = useMemo(() => {
  //   const divisionList = getLocalStorage("permissions", []);
  //   const {
  //     outermostKeys: zone,
  //     outerToInnerKeys: divisionLists,
  //     innerToCustomerNames: station,
  //     customerNameToId: customer_name_to_id,
  //   }: any = extractUserDataFromPermission(JSON.parse(divisionList));

  //   return {
  //     divisionLists,
  //     station,
  //     zone,
  //     customer_name_to_id,
  //   };
  // }, []);

  // const sections = useMemo(() => {
  //   if (privateFilter?.zone) {
  //     return divisionLists[privateFilter.zone] || [];
  //   }
  //   return [];
  // }, [privateFilter?.zone]);

  useEffect(() => {
    const filteredStations = privateFilter.customer.filter((stn: any) => Object.values(station).flat().includes(stn));
    setPrivateFilter((draft: any) => {
      draft.customer = filteredStations;
    });
    // when selected station removed after removing station from global filter. 
    // if(privateFilter.customer.length !== filteredStations.length) {
    onFilter({
      ...privateFilter,
      customer: filteredStations,
    });
    // }
  }, [station]);

  const filteredStations = useMemo(() => {
    // if (privateFilter?.division) {
    //   return station[privateFilter.division] || [];
    // }
    if (!station) return [];
    return (Object.values(station) || []).flat();
    // return staton;
  }, [station]);

  const onChangeFilter = (name: string, value: any | any[]) => {
    setPrivateFilter((draft: any) => {
      draft[name] = value;
    });
  };
  return (
    <div className="filters-container">
      {/* <Autocomplete
        size="small"
        className="filter-input auto-complete"
        options={zone}
        renderInput={(params) => (
          <TextField className="filter-input" {...params} label="Division" placeholder="Division" />
        )}
        getOptionLabel={(customer) => customer}
        isOptionEqualToValue={(option, value) => option === value}
        value={privateFilter?.zone || null}
        onChange={(_, newValue) => {
          onChangeFilter("zone", newValue || "");
          onChangeFilter("division", "");
          onChangeFilter("customer", "");
        }}
      />

      <Autocomplete
        size="small"
        className="filter-input auto-complete"
        options={sections}
        renderInput={(params) => (
          <TextField className="filter-input" {...params} label="Section" placeholder="Section" />
        )}
        getOptionLabel={(option) => option}
        isOptionEqualToValue={(option, value) => option === value}
        value={privateFilter?.division || null}
        onChange={(_, newValue) => {
          onChangeFilter("division", newValue || "");
          onChangeFilter("customer", "");
        }}
      /> */}

      <Autocomplete
        size="small"
        disableCloseOnSelect
        multiple
        className="filter-input auto-complete"
        options={filteredStations}
        renderInput={(params) => (
          <TextField className="filter-input" {...params} label="Stations" placeholder="Stations" />
        )}
        getOptionLabel={(option) => option}
        isOptionEqualToValue={(option, value) => option === value}
        value={privateFilter?.customer || []}
        onChange={(_, newValue) => {
          onChangeFilter("customer", newValue || []);
        }}
        renderTags={(value, getTagProps) => {
          return (
            <div>
              <Chip size="small" variant="outlined" label={value[0]} {...getTagProps({ index: 0 })} />
              {value.length > 1 && (
                <AutocompleteStationListPop title={value.map((stn) => stn ?? "").toString()}>
                  <Chip size="small" label={`+${value.length - 1}`} />
                </AutocompleteStationListPop>
              )}
            </div>
          );
        }}
      />

      <Autocomplete
        size="small"
        className="filter-input auto-complete"
        multiple
        disableCloseOnSelect
        limitTags={2}
        isOptionEqualToValue={(option, value) => option.systemId === value.systemId}
        getOptionKey={(option) => option.systemId}
        value={privateFilter.systems ?? []}
        options={system_options}
        getOptionLabel={(option) => option.systemName}
        onChange={(_, value) =>
          setPrivateFilter((prev: any) => ({
            ...prev,
            systems: value,
          }))
        }
        renderTags={(value, getTagProps) => {
          return (
            <div>
              <Chip size="small" variant="outlined" label={value[0].systemName} {...getTagProps({ index: 0 })} />
              {value.length > 1 && <Chip size="small" label={`+${value.length - 1}`} />}
            </div>
          );
        }}
        renderInput={(params) => <TextField {...params} label="Systems" placeholder="Systems" />}
      />

      <TextField
        size="small"
        fullWidth
        className="filter-input"
        label="Component Name"
        placeholder="Component Name"
        value={privateFilter?.componentName || ""}
        onChange={(e) => {
          onChangeFilter("componentName", e.target.value);
        }}
      />

      <TextField
        size="small"
        fullWidth
        className="filter-input"
        label="Asset Name"
        placeholder="Asset Name"
        value={privateFilter?.gearName || ""}
        onChange={(e) => {
          onChangeFilter("gearName", e.target.value);
        }}
      />

      <TextField
        size="small"
        fullWidth
        className="filter-input"
        label="Make"
        placeholder="Make"
        value={privateFilter?.make || ""}
        onChange={(e) => {
          onChangeFilter("make", e.target.value);
        }}
      />

      <TextField
        size="small"
        fullWidth
        className="filter-input"
        label="Model"
        placeholder="Model"
        value={privateFilter?.model || ""}
        onChange={(e) => {
          onChangeFilter("model", e.target.value);
        }}
      />

      <RangePicker
        placeholder={["Expired Date From", "Expired Date To"]}
        size="large"
        value={[privateFilter?.expireDateFrom || null, privateFilter?.expireDateTo || null]}
        onChange={(dates) => {
          if (dates && dates[0] && dates[1]) {
            onChangeFilter("expireDateFrom", dates[0]);
            onChangeFilter("expireDateTo", dates[1]);
          }
        }}
      />

      <Button variant="contained" onClick={() => onFilter(privateFilter)}>
        Filter
      </Button>
      <Button
        variant="outlined"
        onClick={() => {
          onFilter(initialFilters);
          setPrivateFilter(initialFilters);
        }}
      >
        Clear
      </Button>
    </div>
  );
};
